import React, { useState, useEffect, useCallback } from "react";
import { Link as RouterLink } from "react-router-dom";
import { styled, useTheme, alpha, keyframes } from "@mui/material/styles";
import {
  Dialog, DialogContent, Box, Typography, IconButton,
  Chip, Divider, Button, Tooltip, Collapse,
} from "@mui/material";

import CloseIcon            from "@mui/icons-material/Close";
import AutoAwesomeIcon      from "@mui/icons-material/AutoAwesome";
import BuildOutlinedIcon    from "@mui/icons-material/BuildOutlined";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import WarningAmberIcon     from "@mui/icons-material/WarningAmber";
import ExpandMoreIcon       from "@mui/icons-material/ExpandMore";
import ExpandLessIcon       from "@mui/icons-material/ExpandLess";
import NewReleasesIcon      from "@mui/icons-material/NewReleases";
import RocketLaunchIcon     from "@mui/icons-material/RocketLaunch";
import MenuBookIcon         from "@mui/icons-material/MenuBook";
import EngineeringIcon      from "@mui/icons-material/Engineering";

import { CURRENT_VERSION, RELEASES } from "../../data/changelog";

// ─── Storage key ──────────────────────────────────────────────────────────────

const STORAGE_KEY = "adticket_seen_version";

export const hasUnseenRelease = () => {
  const seen = localStorage.getItem(STORAGE_KEY);
  return seen !== CURRENT_VERSION;
};

export const markAsSeen = () => {
  localStorage.setItem(STORAGE_KEY, CURRENT_VERSION);
};

// ─── Item type config ─────────────────────────────────────────────────────────

const TYPE_CONFIG = {
  new:         { label: "Novo",       color: "#6366f1", bg: alpha("#6366f1", 0.1), icon: <AutoAwesomeIcon /> },
  improvement: { label: "Melhoria",   color: "#0284c7", bg: alpha("#0284c7", 0.1), icon: <BuildOutlinedIcon /> },
  fix:         { label: "Correção",   color: "#10b981", bg: alpha("#10b981", 0.1), icon: <BugReportOutlinedIcon /> },
  breaking:    { label: "Atenção",    color: "#ef4444", bg: alpha("#ef4444", 0.1), icon: <WarningAmberIcon /> },
};

// ─── Pulse animation for badge ────────────────────────────────────────────────

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(239,68,68,0.6); }
  70% { box-shadow: 0 0 0 7px rgba(239,68,68,0); }
  100% { box-shadow: 0 0 0 0 rgba(239,68,68,0); }
`;

export const PulseDot = styled("span")(() => ({
  position: "absolute",
  top: 4, right: 4,
  width: 8, height: 8,
  borderRadius: "50%",
  background: "#ef4444",
  animation: `${pulse} 1.8s ease-in-out infinite`,
}));

// ─── Styled ───────────────────────────────────────────────────────────────────

const TypeBadge = styled(Box, { shouldForwardProp: p => p !== "itemType" })(
  ({ itemType }) => {
    const c = TYPE_CONFIG[itemType] || TYPE_CONFIG.new;
    return {
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "1px 8px", borderRadius: 99, flexShrink: 0,
      background: c.bg, color: c.color,
      fontSize: "0.67rem", fontWeight: 800, letterSpacing: "0.04em",
      textTransform: "uppercase",
      "& svg": { fontSize: 12 },
    };
  }
);

// ─── Single release block ─────────────────────────────────────────────────────

function ReleaseBlock({ release, isLatest }) {
  const theme = useTheme();
  return (
    <Box sx={{ mb: isLatest ? 0 : 3 }}>
      {!isLatest && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
          <Chip
            label={`v${release.version}`}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 700, fontSize: "0.75rem", height: 22 }}
          />
          <Typography variant="caption" color="text.disabled">{release.date}</Typography>
          {release.title && (
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
              {release.title}
            </Typography>
          )}
          <Box sx={{ flex: 1, borderTop: "1px solid", borderColor: "divider" }} />
        </Box>
      )}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {release.items.map((item, i) => (
          <Box key={i} sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
            <TypeBadge itemType={item.type}>
              {React.cloneElement(TYPE_CONFIG[item.type]?.icon || TYPE_CONFIG.new.icon, {})}
              {TYPE_CONFIG[item.type]?.label || "Novo"}
            </TypeBadge>
            <Typography
              fontSize={isLatest ? "0.88rem" : "0.82rem"}
              lineHeight={1.6}
              color={isLatest ? "text.primary" : "text.secondary"}
            >
              {item.text}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ─── Modal ────────────────────────────────────────────────────────────────────

const WhatsNewModal = ({ open, onClose }) => {
  const theme = useTheme();
  const [showHistory, setShowHistory] = useState(false);

  const latest = RELEASES[0];
  const history = RELEASES.slice(1);

  const handleClose = useCallback(() => {
    markAsSeen();
    onClose();
  }, [onClose]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          background: theme.palette.background.paper,
        },
      }}
    >
      {/* Hero */}
      <Box sx={{
        background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
        color: "#fff", px: 3.5, pt: 3.5, pb: 2.5,
        position: "relative",
        "&::before": {
          content: '""', position: "absolute", top: -30, right: -30,
          width: 120, height: 120, borderRadius: "50%", background: alpha("#fff", 0.06),
        },
      }}>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            position: "absolute", top: 12, right: 12,
            color: alpha("#fff", 0.7), "&:hover": { color: "#fff", bgcolor: alpha("#fff", 0.1) },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
          <RocketLaunchIcon sx={{ fontSize: 22 }} />
          <Typography fontWeight={800} fontSize="1.15rem">O que há de novo</Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Chip
            label={`v${latest.version}`}
            size="small"
            sx={{
              bgcolor: alpha("#fff", 0.2), color: "#fff", fontWeight: 800,
              fontSize: "0.78rem", height: 24,
            }}
          />
          <Typography variant="caption" sx={{ opacity: 0.75 }}>{latest.date}</Typography>
        </Box>

        {latest.title && (
          <Typography fontSize="0.88rem" sx={{ mt: 1, opacity: 0.9, lineHeight: 1.5 }}>
            {latest.title}
          </Typography>
        )}

        {latest.highlight && (
          <Typography
            fontSize="0.82rem"
            sx={{ mt: 0.75, opacity: 0.8, fontStyle: "italic", lineHeight: 1.5 }}
          >
            {latest.highlight}
          </Typography>
        )}
      </Box>

      <DialogContent sx={{ px: 3.5, py: 3 }}>
        {/* Latest release items */}
        <ReleaseBlock release={latest} isLatest />

        {/* History toggle */}
        {history.length > 0 && (
          <>
            <Divider sx={{ my: 2.5 }} />
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer", mb: showHistory ? 2 : 0 }}
              onClick={() => setShowHistory(v => !v)}
            >
              <Typography fontSize="0.8rem" fontWeight={700} color="text.secondary">
                Versões anteriores
              </Typography>
              <Chip
                label={history.length}
                size="small"
                sx={{ height: 18, fontSize: "0.65rem", fontWeight: 700 }}
              />
              <Box sx={{ flex: 1 }} />
              {showHistory ? <ExpandLessIcon fontSize="small" sx={{ color: "text.disabled" }} /> : <ExpandMoreIcon fontSize="small" sx={{ color: "text.disabled" }} />}
            </Box>
            <Collapse in={showHistory}>
              {history.map(r => <ReleaseBlock key={r.version} release={r} isLatest={false} />)}
            </Collapse>
          </>
        )}

        {/* Quick links */}
        <Box sx={{ display: "flex", gap: 1, mt: 3, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            size="small"
            component={RouterLink}
            to="/documentation"
            onClick={handleClose}
            startIcon={<MenuBookIcon />}
            sx={{ fontSize: "0.78rem", borderRadius: 1.5, textTransform: "none" }}
          >
            Guia do Sistema
          </Button>
          <Button
            variant="outlined"
            size="small"
            component={RouterLink}
            to="/runbook"
            onClick={handleClose}
            startIcon={<EngineeringIcon />}
            sx={{ fontSize: "0.78rem", borderRadius: 1.5, textTransform: "none" }}
          >
            Manual de Operações
          </Button>
        </Box>

        {/* CTA */}
        <Box sx={{ mt: 2.5, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            onClick={handleClose}
            startIcon={<RocketLaunchIcon />}
            sx={{
              borderRadius: 2, textTransform: "none", fontWeight: 700,
              background: "linear-gradient(135deg, #4f46e5, #7c3aed)",
              "&:hover": { background: "linear-gradient(135deg, #4338ca, #6d28d9)" },
            }}
          >
            Entendi, vamos lá!
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default WhatsNewModal;
